import React, { useEffect, useState } from "react";
import useShowAlert from "../../../utils/AlertUtiles";
import checkNetworkStatus from "../../common/networkStatus/CheckNetworkStatus";
import handleError from "../../../constant/handleError";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../../utils/AxiosInstance";
import DateConverter from "@heartinz/date_and_time_processor";
import uRLs from "../../../utils/baseUtils";
import { capitalizeFirstLetter, getSubBookingStatusLabel } from "../../../utils/helper";
import GangLeaderSubBookingTimeline from "./GangLeaderSubBookingTimeline";
import DailyWorkLogPhoto from "./DailyWorkLogPhoto";

// Multi-Gang-Leader assignment for bookings that support the split-booking
// flow (`booking.supportsSplit === true`). Each Gang Leader added here gets
// their own sub-booking, independent of the primary booking's own status.
// This is a self-contained modal: it fetches and refreshes its own
// allocation list rather than relying on the parent's `isSelectedData`, so
// it stays correct across repeated add/reject cycles without needing the
// parent to refetch the whole booking.
function AssignGangLeaderBatch({ onClose, bookingId, pincode, numberOfAcres }) {
  const [allocations, setAllocations] = useState([]);
  const [remainingAcres, setRemainingAcres] = useState(numberOfAcres);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [subBookingsById, setSubBookingsById] = useState({});
  const [glList, setGlList] = useState([]);
  const [loadingGlList, setLoadingGlList] = useState(false);
  const [selectedGL, setSelectedGL] = useState(null);
  const [allocatedAcresInput, setAllocatedAcresInput] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState("");
  const [expandedLogsSubBookingId, setExpandedLogsSubBookingId] = useState(null);
  const [expandedTimelineSubBookingId, setExpandedTimelineSubBookingId] = useState(null);
  const [decidingSubBookingId, setDecidingSubBookingId] = useState(null);
  const [measurementInputs, setMeasurementInputs] = useState({});
  const [savingMeasurementId, setSavingMeasurementId] = useState(null);
  const showAlert = useShowAlert();

  const fetchAllocations = async () => {
    setLoadingAllocations(true);
    try {
      const response = await axiosInstance.get(`farmerbooking/admin/${bookingId}/batch/allocations`);
      if (response?.data?.status) {
        setAllocations(response.data.results.glAllocations || []);
        setRemainingAcres(response.data.results.remainingAcres);
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setLoadingAllocations(false);
    }
  };

  const fetchSubBookings = async () => {
    try {
      const response = await axiosInstance.get(`farmerbooking/admin/${bookingId}/batch/sub-bookings`);
      if (response?.data?.status) {
        const byId = {};
        (response.data.results || []).forEach((subBooking) => {
          byId[subBooking._id] = subBooking;
        });
        setSubBookingsById(byId);
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const fetchGlList = async () => {
    if (!pincode) {
      showAlert("error", "Booking pincode not found. Cannot fetch relevant Captains.");
      return;
    }
    setLoadingGlList(true);
    checkNetworkStatus();
    try {
      const response = await axiosInstance.get(`gangleader/admin/pincode/list/${pincode}?pageno=1&limit=50`);
      if (response?.data?.status) {
        setGlList(response.data.results || []);
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setLoadingGlList(false);
    }
  };

  useEffect(() => {
    fetchAllocations();
    fetchSubBookings();
    fetchGlList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAssign = async () => {
    setError("");
    const acres = Number(allocatedAcresInput);
    if (!selectedGL) {
      setError("Select a Captain first.");
      return;
    }
    if (!acres || acres <= 0) {
      setError("Enter a valid number of acres.");
      return;
    }
    if (acres > remainingAcres) {
      setError(`Only ${remainingAcres} acres remain unallocated.`);
      return;
    }
    setAssigning(true);
    checkNetworkStatus();
    try {
      const response = await axiosInstance.patch(`farmerbooking/admin/${bookingId}/batch/assign-gl`, {
        gangleaderId: selectedGL.partnerData.partnerId,
        allocatedAcres: acres
      });
      showAlert("success", response?.data?.message || "Captain allocated successfully.");
      setSelectedGL(null);
      setAllocatedAcresInput("");
      await fetchAllocations();
      await fetchSubBookings();
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setAssigning(false);
    }
  };

  // Accepting/rejecting a Gang-Leader-submitted measurement that's pending
  // Field Officer/admin decision. Reuses the same endpoint the FO's own
  // Partner-App route now calls (`.../measurement/fo-decision`) — the backend
  // doesn't distinguish who called it, only that the caller is authorized to.
  const handleMeasurementDecision = async (subBookingId, decision) => {
    setDecidingSubBookingId(subBookingId);
    checkNetworkStatus();
    try {
      const response = await axiosInstance.patch(`farmerbooking/admin/batch/${subBookingId}/measurement/fo-decision`, { decision });
      if (response?.data?.status) {
        showAlert("success", response?.data?.message || "Measurement decision recorded successfully.");
        await fetchSubBookings();
        await fetchAllocations();
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setDecidingSubBookingId(null);
    }
  };

  // Admin entering/redoing a Gang Leader's measurement on their behalf —
  // mirrors the legacy single-booking "Update Measurement" flow, just scoped
  // to one sub-booking. This is also how a REJECTED_NEEDS_FO sub-booking gets
  // resolved: admin re-enters a corrected value here.
  const handleSaveMeasurement = async (subBookingId) => {
    const value = Number(measurementInputs[subBookingId]);
    if (!value || value <= 0) {
      showAlert("error", "Enter a valid number of acres.");
      return;
    }
    setSavingMeasurementId(subBookingId);
    checkNetworkStatus();
    try {
      const response = await axiosInstance.patch(`farmerbooking/admin/batch/${subBookingId}/measurement`, { measurementInAcres: value });
      if (response?.data?.status) {
        showAlert("success", response?.data?.message || "Measurement recorded successfully.");
        setMeasurementInputs((prev) => ({ ...prev, [subBookingId]: "" }));
        await fetchSubBookings();
        await fetchAllocations();
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setSavingMeasurementId(null);
    }
  };

  const statusBadgeClass = (status) => {
    if (status === "ACCEPTED") return "bg-label-success";
    if (status === "REJECTED") return "bg-label-danger";
    return "bg-label-warning";
  };

  return (
    <>
      <div className="modal-backdrop show"></div>
      <div className="modal show d-block" tabIndex="-1" aria-modal="true" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-bottom-0 py-3">
              <h6 className="modal-title fw-semibold">Captain Batch Allocation</h6>
              <button type="button" className="btn-close border" onClick={onClose} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="d-flex justify-content-between align-items-center mb-3 p-2 border rounded-3 bg-light">
                <span>Required: <strong>{numberOfAcres} acres</strong></span>
                <span>Allocated: <strong>{numberOfAcres - remainingAcres} acres</strong></span>
                <span>Remaining: <strong className={remainingAcres > 0 ? "text-danger" : "text-success"}>{remainingAcres} acres</strong></span>
              </div>

              <h6 className="fw-bold font-13 mb-2">Current Allocations</h6>
              {loadingAllocations ? (
                <div className="text-center mb-3"><CircularProgress size={20} /></div>
              ) : allocations.length === 0 ? (
                <p className="text-muted font-12 mb-3">No Captain allocated yet.</p>
              ) : (
                <div className="list-group mb-3">
                  {allocations.map((a) => {
                    const subBooking = subBookingsById[a.subBookingId];
                    const dailyWorkLogs = subBooking?.dailyWorkLogs || [];
                    const isLogsExpanded = expandedLogsSubBookingId === a.subBookingId;
                    const isTimelineExpanded = expandedTimelineSubBookingId === a.subBookingId;
                    return (
                    <div key={a._id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{capitalizeFirstLetter(a.gangleaderName) || "-"}</strong>{" "}
                          <span className="text-muted font-12">{a.gangleaderMobile}</span>
                          <div className="font-12 text-muted">{a.allocatedAcres} acres</div>
                          {a.status === "REJECTED" && a.rejectionReason && (
                            <div className="font-12 text-danger">Rejected: {a.rejectionReason}</div>
                          )}
                        </div>
                        <div className="text-end">
                          <span className={`badge ${statusBadgeClass(a.status)}`}>{a.status.replace(/_/g, " ")}</span>
                          {subBooking && (
                            <div className="font-12 text-muted mt-1">{getSubBookingStatusLabel(subBooking.lastStatus)}</div>
                          )}
                        </div>
                      </div>
                      {subBooking && (
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top">
                          <span className={`badge ${subBooking.isGlMeasurementAccepted ? "bg-label-success" : "bg-label-secondary"}`}>
                            {subBooking.isGlMeasurementAccepted ? "✓ Measurement Accepted" : "Measurement Pending"}
                          </span>
                          <span className={`badge ${subBooking.isGlWorkCompleted ? "bg-label-success" : "bg-label-secondary"}`}>
                            {subBooking.isGlWorkCompleted ? "✓ Work Completed" : "Work In Progress"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary ms-auto py-0"
                            onClick={() => setExpandedTimelineSubBookingId(isTimelineExpanded ? null : a.subBookingId)}
                          >
                            {isTimelineExpanded ? "Hide" : "View"} Timeline
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary py-0"
                            onClick={() => setExpandedLogsSubBookingId(isLogsExpanded ? null : a.subBookingId)}
                          >
                            {isLogsExpanded ? "Hide" : "View"} Daily Logs
                            {dailyWorkLogs.length > 0 && ` (${dailyWorkLogs.length})`}
                          </button>
                        </div>
                      )}
                      {isTimelineExpanded && (
                        <div className="mt-2 pt-2 border-top">
                          <GangLeaderSubBookingTimeline subBooking={subBooking} />
                        </div>
                      )}
                      {subBooking && subBooking.currentMeasurementStatus === "PENDING_FO" && (() => {
                        const latest = subBooking.glMeasurementHistory?.[subBooking.glMeasurementHistory.length - 1];
                        return (
                          <div className="alert alert-warning mt-2 mb-0 py-2 px-3 font-13" role="alert">
                            <div className="mb-2">
                              Captain submitted <b>{latest?.measurementInAcres} acres</b>
                              {latest?.previousAcres != null && <> (previously {latest.previousAcres} acres)</>} — needs your approval.
                            </div>
                            {decidingSubBookingId === a.subBookingId ? (
                              <button className="btn btn-sm btn-success" disabled><CircularProgress size={14} className="text-white" /></button>
                            ) : (
                              <div className="d-flex gap-2">
                                <button type="button" className="btn btn-sm btn-success" onClick={() => handleMeasurementDecision(a.subBookingId, "ACCEPT")}>
                                  <i className="bx bx-check"></i> Approve
                                </button>
                                <button type="button" className="btn btn-sm btn-danger" onClick={() => handleMeasurementDecision(a.subBookingId, "REJECT")}>
                                  <i className="bx bx-x"></i> Reject
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {subBooking && subBooking.currentMeasurementStatus === "PENDING_FARMER" && (
                        <div className="font-12 text-muted mt-2">Waiting for farmer approval on a higher measurement.</div>
                      )}

                      {subBooking && subBooking.currentMeasurementStatus === "REJECTED_NEEDS_FO" && (
                        <div className="alert alert-danger mt-2 mb-0 py-2 px-3 font-13" role="alert">
                          {subBooking.foInterventionReason || "Measurement was rejected and needs to be re-entered."}
                        </div>
                      )}

                      {subBooking && !subBooking.isGlWorkCompleted && !["PENDING_FARMER", "PENDING_FO"].includes(subBooking.currentMeasurementStatus) && (
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="Enter acres"
                            value={measurementInputs[a.subBookingId] ?? ""}
                            onChange={(e) => setMeasurementInputs((prev) => ({ ...prev, [a.subBookingId]: e.target.value }))}
                            step="0.1"
                            min="0"
                            style={{ maxWidth: "130px" }}
                          />
                          {savingMeasurementId === a.subBookingId ? (
                            <button className="btn btn-sm btn-primary" disabled><CircularProgress size={14} className="text-white" /></button>
                          ) : (
                            <button type="button" className="btn btn-sm btn-primary" onClick={() => handleSaveMeasurement(a.subBookingId)}>
                              <i className="bx bx-check"></i>{" "}
                              {subBooking.currentMeasurementStatus === "REJECTED_NEEDS_FO" ? "Re-enter Measurement" : "Update Measurement"}
                            </button>
                          )}
                        </div>
                      )}

                      {isLogsExpanded && (() => {
                        const target = Number(a.allocatedAcres) || 0;
                        const completed = dailyWorkLogs.reduce((sum, log) => sum + (Number(log.acresCompletedToday) || 0), 0);
                        const progressPct = target > 0 ? Math.min(100, (completed / target) * 100) : 0;
                        return (
                          <div className="mt-2 p-3 border rounded-3 bg-light">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="font-13">Target</span>
                              <span className="font-13 fw-bold">{target} Acres</span>
                            </div>
                            <div className="progress mb-3" style={{ height: 8 }}>
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <div className="text-muted font-11 fw-bold mb-2" style={{ letterSpacing: "0.5px" }}>
                              WORK UPDATES LOG
                            </div>
                            {dailyWorkLogs.length === 0 ? (
                              <p className="text-muted font-12 mb-0">No entries yet.</p>
                            ) : (
                              dailyWorkLogs.map((log, idx) => {
                                const { date, time } = log.date
                                  ? DateConverter.ISOToDisplayFormat(DateConverter.convertToTimeZone(log.date, uRLs.TIMEZONE))
                                  : { date: "-", time: "" };
                                return (
                                  <div key={idx} className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 mb-2" style={{ background: "#fff" }}>
                                    <DailyWorkLogPhoto photo={log.photo} />
                                    <div className="flex-grow-1">
                                      <div className="font-12 text-muted">{date}{time ? `, ${time}` : ""}</div>
                                      {log.note && <div className="font-11 text-muted">{log.note}</div>}
                                    </div>
                                    <span className="font-13 fw-bold text-success">+{log.acresCompletedToday} Acres</span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    );
                  })}
                </div>
              )}

              {remainingAcres > 0 && (
                <>
                  <h6 className="fw-bold font-13 mb-2">Add Captain</h6>
                  <div className="input-group mb-2">
                    <span className="input-group-text">Acres</span>
                    <input
                      type="number"
                      className="form-control"
                      min="0.01"
                      max={remainingAcres}
                      step="0.01"
                      placeholder={`Up to ${remainingAcres}`}
                      value={allocatedAcresInput}
                      onChange={(e) => setAllocatedAcresInput(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-danger font-12">{error}</p>}
                  {loadingGlList ? (
                    <div className="text-center mt-2"><CircularProgress size={20} /></div>
                  ) : glList.length === 0 ? (
                    <p className="text-center py-2 mb-0">No Captains found for this pincode.</p>
                  ) : (
                    <div className="list-group border rounded-4" style={{ maxHeight: 220, overflowY: "auto" }}>
                      {glList.map((gl) => (
                        <div
                          key={gl._id}
                          className={`list-group-item d-flex justify-content-between align-items-center ${selectedGL?._id === gl._id ? "active" : ""}`}
                          onClick={() => setSelectedGL(gl)}
                          style={{ cursor: "pointer", padding: "10px" }}
                        >
                          <div className={selectedGL?._id === gl._id ? "text-success font-12" : "text-muted font-12"}>
                            <strong>{gl?.partnerData?.mobileNumber}</strong>
                            <div>{capitalizeFirstLetter(gl?.partnerData?.fullName) || "-"} {gl?.partnerData?.capacityInAcres ? `(${gl.partnerData.capacityInAcres} acres capacity)` : ""}</div>
                          </div>
                          {selectedGL?._id === gl._id && <i className="bx bx-check-circle fs-6 text-success"></i>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer border-0 pt-0">
              <button type="button" className="btn btn-outline-secondary px-3" onClick={onClose}>Close</button>
              {remainingAcres > 0 && (
                assigning ? (
                  <button className="btn btn-primary" disabled><CircularProgress size={20} className="text-white" /></button>
                ) : (
                  <button type="button" className="btn btn-primary" disabled={!selectedGL || !allocatedAcresInput} onClick={handleAssign}>
                    <i className="bx bx-check me-1"></i> Add
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AssignGangLeaderBatch;
